import {
  fetchOrganization,
  fetchOrganizationProjectsPage,
  fetchOrganizations,
  fetchProject,
} from '@/features/organization-project-picker/api/organizationProjects';
import {
  ORGANIZATION_PROJECT_PAGE_SIZE,
  ORGANIZATION_PROJECT_PICKER_KEY,
  ORGANIZATION_PROJECT_STALE_TIME,
} from '@/features/organization-project-picker/config/organizationProjectPicker';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

const organizationProjectKeys = {
  root: [ORGANIZATION_PROJECT_PICKER_KEY] as const,
  organizations: (term?: string) =>
    [ORGANIZATION_PROJECT_PICKER_KEY, 'organizations', 'infinite', term ?? ''] as const,
  organization: (organizationId: string) =>
    [ORGANIZATION_PROJECT_PICKER_KEY, 'organization', organizationId] as const,
  organizationProjects: (organizationId: string) =>
    [
      ORGANIZATION_PROJECT_PICKER_KEY,
      'organization',
      organizationId,
      'projects',
      'infinite',
    ] as const,
  project: (projectId: string) => [ORGANIZATION_PROJECT_PICKER_KEY, 'project', projectId] as const,
};

export function organizationsInfiniteQuery(q?: string) {
  const term = q?.trim() || undefined;
  return infiniteQueryOptions({
    queryKey: organizationProjectKeys.organizations(term),
    queryFn: ({ pageParam, signal }) =>
      fetchOrganizations(
        { q: term, cursor: pageParam, limit: ORGANIZATION_PROJECT_PAGE_SIZE },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: ORGANIZATION_PROJECT_STALE_TIME,
  });
}

export function useOrganizations(q?: string, enabled = true) {
  return useInfiniteQuery({ ...organizationsInfiniteQuery(q), enabled });
}

export function organizationQuery(organizationId: string) {
  return queryOptions({
    queryKey: organizationProjectKeys.organization(organizationId),
    queryFn: ({ signal }) => fetchOrganization(organizationId, { signal }),
    enabled: !!organizationId,
    staleTime: ORGANIZATION_PROJECT_STALE_TIME,
  });
}

export function useOrganization(organizationId: string) {
  return useQuery(organizationQuery(organizationId));
}

export function organizationProjectsInfiniteQuery(organizationId: string) {
  return infiniteQueryOptions({
    queryKey: organizationProjectKeys.organizationProjects(organizationId),
    queryFn: ({ pageParam, signal }) =>
      fetchOrganizationProjectsPage(
        organizationId,
        { cursor: pageParam, limit: ORGANIZATION_PROJECT_PAGE_SIZE },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    staleTime: ORGANIZATION_PROJECT_STALE_TIME,
  });
}

export function useOrganizationProjects(organizationId: string, enabled = true) {
  return useInfiniteQuery({
    ...organizationProjectsInfiniteQuery(organizationId),
    enabled: enabled && !!organizationId,
  });
}

export function projectQuery(projectId: string) {
  return queryOptions({
    queryKey: organizationProjectKeys.project(projectId),
    queryFn: ({ signal }) => fetchProject(projectId, { signal }),
    enabled: !!projectId,
    staleTime: ORGANIZATION_PROJECT_STALE_TIME,
  });
}

export function useProject(projectId: string) {
  return useQuery(projectQuery(projectId));
}
